import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, BoxesIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, SlidersHorizontalIcon, TrendingUpIcon } from 'lucide-react';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { RecoveryRules, defaultRecoveryRules, rulesMatchDefaults, summarizeRules } from '../data/recoveryRules';
import { Batch, eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
import { SubmissionRules } from './SubmissionRules';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
type BrandBase = {
  receivedProducts: number;
  receivedUnits: number;
  totalMsrp: number;
  maxOpportunity: number;
  soldUnits: number;
  totalSales: number;
};
type BatchRow = Batch & {
  brand: string;
  id: string;
};
type SortKey = 'uploaded' | 'batch' | 'brand' | 'units' | 'msrp' | 'recovery';
const ROWS_PER_PAGE = 50;

// Submission dates arrive as display strings ("Feb 5, 2026") and can be missing.
function uploadedTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
const onePeopleBase: BrandBase = {
  receivedProducts: 189,
  receivedUnits: 1902,
  totalMsrp: 130993,
  maxOpportunity: 65437,
  soldUnits: 2920,
  totalSales: 49411
};
const eddyBase: BrandBase = {
  receivedProducts: 320,
  receivedUnits: 2981,
  totalMsrp: 321656,
  maxOpportunity: 160828,
  soldUnits: 4010,
  totalSales: 325875
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const nisoloActive = includes('Nisolo');
  const {
    inventory: nisolo
  } = useNisoloProducts(nisoloActive);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(nisoloActive);
  const {
    data: nisoloOrders
  } = useNisoloOrders(nisoloActive);
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  const [openRules, setOpenRules] = useState<string | null>(null);
  const [rulesById, setRulesById] = useState<Record<string, RecoveryRules>>({});
  const rulesFor = (id: string) => rulesById[id] ?? defaultRecoveryRules;
  const setRulesFor = (id: string, rules: RecoveryRules) => setRulesById(current => ({
    ...current,
    [id]: rules
  }));
  const resetRulesFor = (id: string) => setRulesById(current => {
    const {
      [id]: _removed,
      ...rest
    } = current;
    return rest;
  });
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: 'asc' | 'desc';
  }>({
    key: 'uploaded',
    direction: 'desc'
  });
  const [page, setPage] = useState(1);
  useEffect(() => setDeselected([]), [selectedOwner]);
  useEffect(() => setPage(1), [selectedOwner, sort]);
  const toggleSort = (key: SortKey) => setSort(current => current.key === key ? {
    key,
    direction: current.direction === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    direction: key === 'batch' || key === 'brand' ? 'asc' : 'desc'
  });
  const brands = useMemo(() => {
    const entries: {
      brand: string;
      batches: Batch[];
      base: BrandBase;
    }[] = [];
    if (includes('1 People')) entries.push({
      brand: '1 People',
      batches: onePeopleIncomingBatches,
      base: onePeopleBase
    });
    if (includes('Eddy')) entries.push({
      brand: 'Eddy',
      batches: eddyIncomingBatches,
      base: eddyBase
    });
    if (nisoloActive) {
      entries.push({
        brand: 'Nisolo',
        batches: nisoloSubmissions?.submissions ?? [],
        base: {
          receivedProducts: nisolo?.totalProducts ?? 0,
          receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
          totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
          maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
          soldUnits: nisoloOrders?.units ?? 0,
          totalSales: nisoloOrders?.sales ?? 0
        }
      });
    }
    return entries;
  }, [selectedOwner, nisolo, nisoloSubmissions, nisoloOrders]);
  const unsortedRows: BatchRow[] = useMemo(
  // Batch names repeat within a brand, so the index keeps each row's id unique.
  () => brands.flatMap(({
    brand,
    batches
  }) => batches.map((batch, index) => ({
    ...batch,
    brand,
    id: submissionId(brand, index, batch.name)
  }))), [brands]);
  const rows: BatchRow[] = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const value = (row: BatchRow) => {
      switch (sort.key) {
        case 'uploaded':
          return uploadedTime(row.arrived);
        case 'batch':
          return row.name.toLocaleLowerCase();
        case 'brand':
          return row.brand.toLocaleLowerCase();
        case 'units':
          return row.received;
        case 'msrp':
          return row.msrp ?? 0;
        case 'recovery':
          return row.estimatedRecovery ?? 0;
      }
    };
    return [...unsortedRows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [unsortedRows, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const isSelected = (row: BatchRow) => !deselected.includes(row.id);
  const toggleBatch = (row: BatchRow) => setDeselected(current => current.includes(row.id) ? current.filter(id => id !== row.id) : [...current, row.id]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? rows.map(row => row.id) : []);

  // Each brand contributes only its selected submissions. Brands whose batch
  // records carry no valuation fall back to a share of their known totals.
  const totals = useMemo(() => {
    return brands.reduce((sum, {
      brand,
      batches,
      base
    }) => {
      const selected = batches.filter((batch, index) => !deselected.includes(`${brand}::${index}::${batch.name}`));
      const brandUnits = batches.reduce((count, batch) => count + batch.received, 0);
      const selectedUnits = selected.reduce((count, batch) => count + batch.received, 0);
      const share = batches.length === 0 ? 1 : brandUnits > 0 ? selectedUnits / brandUnits : selected.length / batches.length;
      const scale = (value: number) => Math.round(value * share);
      const lines = selected.reduce((count, batch) => count + (batch.lineItems ?? 0), 0);
      const batchMsrp = selected.reduce((value, batch) => value + (batch.msrp ?? 0), 0);
      const batchRecovery = selected.reduce((value, batch) => value + (batch.estimatedRecovery ?? 0), 0);
      const matched = brand === 'Nisolo' ? selected.reduce((result, submission) => {
        const outcome = reconcileSubmission(submission, nisoloOrders);
        return {
          units: result.units + outcome.soldUnits,
          sales: result.sales + outcome.actualSales
        };
      }, {
        units: 0,
        sales: 0
      }) : {
        units: scale(base.soldUnits),
        sales: scale(base.totalSales)
      };
      return {
        products: sum.products + (lines > 0 ? lines : scale(base.receivedProducts)),
        lines: sum.lines + lines,
        units: sum.units + (batches.length > 0 ? selectedUnits : base.receivedUnits),
        msrp: sum.msrp + (batchMsrp > 0 ? batchMsrp : scale(base.totalMsrp)),
        recovery: sum.recovery + (batchRecovery > 0 ? batchRecovery : scale(base.maxOpportunity)),
        soldUnits: sum.soldUnits + matched.units,
        totalSales: sum.totalSales + matched.sales
      };
    }, {
      products: 0,
      lines: 0,
      units: 0,
      msrp: 0,
      recovery: 0,
      soldUnits: 0,
      totalSales: 0
    });
  }, [brands, deselected, nisoloOrders]);
  const receivedProducts = totals.products;
  const receivedUnits = totals.units;
  const selectedLines = totals.lines;
  const predictedRecovery = totals.recovery;
  const soldUnits = totals.soldUnits;
  const totalSales = totals.totalSales;
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }, {
    label: 'Units sold',
    value: soldUnits.toLocaleString('en-US'),
    detail: 'Matched to selected uploads'
  }, {
    label: 'Total sales',
    value: currency(totalSales),
    detail: 'Gross recovery revenue'
  }, {
    label: 'Actual recovery / unit',
    value: perUnit(totalSales, soldUnits),
    detail: soldUnits > 0 ? 'Per unit sold' : 'No sales yet'
  }];
  const msrpValue = totals.msrp;
  const totalMsrp = currency(msrpValue);
  const maxOpportunity = currency(predictedRecovery);
  const totalReceivedValue = currency(msrpValue);
  const overstockDetail = `${receivedUnits.toLocaleString('en-US')} units across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1358" data-mp="apaiqe89xf7s">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1359" data-mp="5wqw6rrlz9qu">
        <div data-id="element-1360" data-mp="kklr8et39q73">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1361" data-mp="ko9krdyjaclt">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1362" data-mp="tmuxutl8kzf7" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1363" data-mp="oniewjxhkchw">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1364" data-mp="2vgkkpvsyphob">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1365" data-mp="zajtsnecw4nq">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1366" data-mp="148v6d5w1ct9n" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1367" data-mp="1db4c7igczygp">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1368" data-mp="1n1630i03juzb">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1369" data-mp="3lk13117y56rv">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1370" data-mp="3gjmu7gyitsss" />
        </div> : <>
      <section className="mt-6 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-1371" data-mp="3ci1i3xtt9ijn">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1372" data-mp="37j87cd5ws5n6">
          <div className="flex items-center gap-2" data-id="element-1373" data-mp="82s28wj9hsth">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1374" data-mp="85o24qeo6rkk" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1375" data-mp="431butxndeph">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-1376" data-mp="3vf2uw4tlwmy5">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-1377" data-mp="rru167jrkob4" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1378" data-mp="mz9ahew5mrbt">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1379" data-mp="6k1kxxu8lsw2">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1380" data-mp="bil5pux3gymu">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-1381" data-mp="2jhl2mia4b68">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-1382" data-mp="qa3rk9cmxne4">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-1383" data-mp="ubfc40gnjifg" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-1384" data-mp="gk0r5o36p75r">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-1385" data-mp="li2becccjcit">
            <div data-id="element-1386" data-mp="lgitkhb1ktex">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1387" data-mp="198pw5y7d1wms">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1388" data-mp="1dywkdwxynf4i">{totalMsrp}</p>
            </div>
            <div data-id="element-1389" data-mp="1nd6my28qd6sw">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1390" data-mp="1ncngbhoj64u6">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1391" data-mp="1jbv6am7u3vwu">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-1392" data-mp="1s4op2tmhpdfg">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-1393" data-mp="10dins9v199ol">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-1394" data-mp="10fllzh6ca94e">
          <div className="flex items-center gap-2" data-id="element-1395" data-mp="19e6zgxwyexyb">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1396" data-mp="19dfhipwa4yqi" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-1397" data-mp="2pre0fu3312dz">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-1398" data-mp="2ksavsar0ky6j">Total MSRP value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-1399" data-mp="2sul5twmpckty">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-1400" data-mp="2wuvly247krmj">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-1401" data-mp="31uqc23g5td6j">
            <div className="flex items-start gap-2" data-id="element-1402" data-mp="31vjbejcsh0en">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1403" data-mp="36ucq2f3taxge" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-1404" data-mp="296u7kckcmgz7">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1405" data-mp="2j2nvst7fjywt">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1406" data-mp="2nws1za8vg2wu">
            <div className="flex items-center gap-2" data-id="element-1407" data-mp="1veo2pmmnbwf2">
              <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1408" data-mp="1vd37rvwf6bgo" />
              <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1409" data-mp="1ntu0c87h4mu9">Inventory submissions</h2>
            </div>
            {pageCount > 1 && <nav className="flex items-center gap-1.5 text-[13px]" aria-label="Submission pages" data-id="element-1410" data-mp="1dwz0nlzgsgee">
                <button type="button" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Previous page" data-id="element-1411" data-mp="18xw4ys5djmj3">
                  <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1412" data-mp="q8piqv5dgcsj" />
                </button>
                <span className="tabular-nums text-ink/60" data-id="element-1413" data-mp="l7s42sjkm0vs">
                  Page <span className="font-semibold text-ink" data-id="element-1414" data-mp="h66mwv0070fs">{currentPage}</span> of {pageCount}
                </span>
                <button type="button" onClick={() => setPage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Next page" data-id="element-1415" data-mp="254ftgxuwup7x">
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1416" data-mp="1r3mvu49bbe0l" />
                </button>
              </nav>}
            <p className="ml-auto text-[13px] text-ink/50" data-id="element-1417" data-mp="1cfs1kjfnp1le">{rows.length.toLocaleString('en-US')} submissions</p>
          </div>
          <div className="max-h-[420px] overflow-auto" data-id="element-1418" data-mp="1he3amry4n82k">
            <table className="w-full min-w-[880px] text-left text-sm" data-id="element-1419" data-mp="13diu62fopoc8">
              <thead className="sticky top-0 z-10 bg-cream text-xs text-ink/50" data-id="element-1420" data-mp="17fmgjm3w6dca">
                <tr data-id="element-1421" data-mp="17hqgxu2l0u7t">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1422" data-mp="orc0noxwd3qy">
                    <span className="flex items-center gap-2.5" data-id="element-1423" data-mp="ot6kt11cgrtz">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1424" data-mp="jttn1l8xq0cm" />
                      <SortButton label="Uploaded" sortKey="uploaded" sort={sort} onSort={toggleSort} data-id="element-1425" data-mp="xtbgwf1383su" />
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1426" data-mp="17po7v55903jw"><SortButton label="Batch" sortKey="batch" sort={sort} onSort={toggleSort} data-id="element-1427" data-mp="17od7lfb0hdxj" /></th>
                  {allBrands && <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1428" data-mp="12p0h8kkxloq7"><SortButton label="Brand" sortKey="brand" sort={sort} onSort={toggleSort} data-id="element-1429" data-mp="1ghget5v2cujq" /></th>}
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1430" data-mp="1chz7s7zznpcg"><SortButton label="Unit count" sortKey="units" sort={sort} onSort={toggleSort} align="right" data-id="element-1431" data-mp="1qgp6tt6dbz3g" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1432" data-mp="1li4f6i56mnqm"><SortButton label="MSRP value" sortKey="msrp" sort={sort} onSort={toggleSort} align="right" data-id="element-1433" data-mp="1lfi07xfyp5yd" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1434" data-mp="1t19hyh6f5ty3"><SortButton label="Estimated recovery" sortKey="recovery" sort={sort} onSort={toggleSort} align="right" data-id="element-1435" data-mp="1t0q359c2floa" /></th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1436" data-mp="27w5vn9wzz9ik">Recovery rules</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1437" data-mp="1faw8gadvq76n">
                {pageRows.length > 0 ? pageRows.map(row => {
                  const checked = isSelected(row);
                  const rules = rulesFor(row.id);
                  const custom = !rulesMatchDefaults(rules);
                  const expanded = openRules === row.id;
                  return <React.Fragment key={row.id} data-id="element-1438" data-mp="wj3iypy0r43o">
                    <tr className={checked ? '' : 'opacity-45'} data-id="element-1439" data-mp="whsfkr8hjyd3">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1440" data-mp="rl20a64i96sq">
                        <span className="flex items-center gap-2.5" data-id="element-1441" data-mp="3373tu9j49ejt">
                          <SelectBox checked={checked} onChange={() => toggleBatch(row)} label={`Include ${row.name} in totals`} data-id="element-1442" data-mp="335styksskqxz" />
                          {row.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1443" data-mp="2y7zmzpjnzswn">{row.name}</th>
                      {allBrands && <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-1444" data-mp="3lz52g3lp0j6f">{row.brand}</td>}
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1445" data-mp="3qw56k8yzfbuw">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1446" data-mp="3czhz3c9el3aq">{row.msrp && row.msrp > 0 ? currency(row.msrp) : '—'}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1447" data-mp="21hqm40uzyb57">{row.estimatedRecovery && row.estimatedRecovery > 0 ? currency(row.estimatedRecovery) : '—'}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-right sm:px-6" data-id="element-1448" data-mp="c14sbvbdu6k8">
                        <button type="button" onClick={() => setOpenRules(expanded ? null : row.id)} aria-expanded={expanded} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${custom ? 'border-[#BB5E40] bg-[#BB5E40]/8 text-[#94452E]' : 'border-ink/15 text-ink/60 hover:border-[#BB5E40]/40 hover:text-ink'}`} data-id="element-1449" data-mp="c2z3wj0rkmbn">
                          <SlidersHorizontalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1450" data-mp="3fidakga0v8fh" />
                          {custom ? summarizeRules(rules) : 'Account defaults'}
                          <ChevronUpIcon className={`h-3 w-3 transition-transform ${expanded ? '' : 'rotate-180'}`} aria-hidden="true" data-id="element-1451" data-mp="1dqqg24qx8ve9" />
                        </button>
                      </td>
                    </tr>
                    {expanded && <tr data-id="element-1452" data-mp="1h6bdz47odecs">
                        <td colSpan={allBrands ? 7 : 6} className="bg-cream/30 px-5 pb-5 sm:px-6" data-id="element-1453" data-mp="1l9rcy777vqsn">
                          <SubmissionRules batchName={row.name} rules={rules} onChange={next => setRulesFor(row.id, next)} onReset={() => resetRulesFor(row.id)} data-id="element-1454" data-mp="1q0zigs5fes0j" />
                        </td>
                      </tr>}
                    </React.Fragment>;
                }) : <tr data-id="element-1455" data-mp="178x1ffovh9pr">
                    <td colSpan={allBrands ? 7 : 6} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1456" data-mp="1c9ba8zyasnou">No inventory submissions yet.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {nisoloActive && <section className="mt-5" aria-label="AI insights" data-id="element-1457" data-mp="g0wssg3xn9w">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter((submission, index) => !deselected.includes(`Nisolo::${index}::${submission.name}`))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1458" data-mp="gkc01edhxk8" />
        </section>}
        </>}
    </div>;
}
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left'
}: {
  label: string;
  sortKey: SortKey;
  sort: {
    key: SortKey;
    direction: 'asc' | 'desc';
  };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink ${active ? 'text-ink' : 'text-ink/50'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1459" data-mp="o5v46uhnqjmr">
      {label}
      <ChevronUpIcon className={`h-3 w-3 shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-0'} ${active && sort.direction === 'desc' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1460" data-mp="1grtk9x411sxn" />
    </button>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1461" data-mp="1bv3316ghkran">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1462" data-mp="t3acdiow3i63" />}
    </button>;
}